import React, { useEffect, useState } from 'react';
import { Amplify } from 'aws-amplify';
import { fetchAuthSession, updatePassword } from 'aws-amplify/auth';
import LoginShell from './components/LoginShell';
import { BrowserRouter, Routes, Route, NavLink, Navigate } from 'react-router-dom';
import { awsConfig } from './config/aws';
import { api } from './services/api';
import Dashboard from './pages/Dashboard';
import Recordings from './pages/Recordings';
import Search from './pages/Search';
import SftpSetup from './pages/SftpSetup';
import AccessControl from './pages/AccessControl';
import Users from './pages/Users';
import Quarantine from './pages/Quarantine';
import ApiKeys from './pages/ApiKeys';
import ExportFormats from './pages/ExportFormats';
import Operations from './pages/Operations';
import GhlLocations from './pages/GhlLocations';
import NasStatusPill from './components/NasStatusPill';
import ComplianceReport from './pages/ComplianceReport';
import SystemView from './pages/SystemView';
import DownloadCart from './pages/DownloadCart';
import UploadCalls from './pages/UploadCalls';
import StatusPage from './pages/StatusPage';
import { DownloadCartProvider, useDownloadCart } from './services/downloadCart';
import { AgentAliasProvider } from './services/agentAliasContext';
import AgentAliases from './pages/AgentAliases';
import ExportSms from './pages/ExportSms';
import '@aws-amplify/ui-react/styles.css';
import './App.css';

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: awsConfig.cognito.userPoolId,
      userPoolClientId: awsConfig.cognito.userPoolClientId,
    },
  },
});

function AppContent({ signOut, user }: { signOut?: () => void; user?: any }) {
  const [isAdmin, setIsAdmin] = useState(false);
  const [canExportSms, setCanExportSms] = useState(false);
  const [roleLoaded, setRoleLoaded] = useState(false);
  const [alertCount, setAlertCount] = useState(0);
  const [alertCountStale, setAlertCountStale] = useState(false);
  const [showChangePw, setShowChangePw] = useState(false);
  const [oldPw, setOldPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [pwMessage, setPwMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);
  const [changingPw, setChangingPw] = useState(false);

  useEffect(() => {
    fetchAuthSession().then(session => {
      const payload = session.tokens?.idToken?.payload;
      const groups: string[] = (payload?.['cognito:groups'] as string[]) || [];
      const admin = groups.includes('admin');
      setIsAdmin(admin);
      setCanExportSms(admin || groups.includes('sms_export'));
      setRoleLoaded(true);
    }).catch(() => setRoleLoaded(true));
  }, []);

  // Sidebar badge count = actionable failures only (download_failed +
  // upload_failed). Quarantine + Flagged-Metadata are audit-only — admins
  // can't auto-fix them, so they don't belong on a "look at this NOW" badge.
  // Their lists load on demand when the user opens the Quarantine page.
  // Both actionable endpoints query the AttentionIndex GSI (cheap, ~5 RCU).
  useEffect(() => {
    if (!roleLoaded) return;
    const checkAlerts = () => {
      Promise.all([
        api.getDownloadFailures(),
        api.getUploadFailures(),
      ]).then(([df, uf]: any[]) => {
        setAlertCount((df.failures || []).length + (uf.failures || []).length);
        setAlertCountStale(false);
      }).catch(() => {
        // Don't silently reset to 0 — keep the last known count and flag it
        // as stale so the badge tooltip explains why it may be off.
        setAlertCountStale(true);
      });
    };
    checkAlerts();
    // Was a 15-min poll, so the sidebar badge lagged the live Quarantine-page
    // counts and looked "wrong" (e.g. badge 25 while the page showed 26).
    // Poll more often AND refresh whenever the tab regains focus, so the badge
    // stays in sync with what the user actually sees on the page.
    const interval = setInterval(checkAlerts, 180000); // 3 min
    window.addEventListener('focus', checkAlerts);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', checkAlerts);
    };
  }, [roleLoaded]);

  if (!roleLoaded) return <div className="loading">Loading...</div>;

  return (
    <BrowserRouter>
      <DownloadCartProvider>
      <AgentAliasProvider>
      <AppInner signOut={signOut} user={user} isAdmin={isAdmin} canExportSms={canExportSms}
        alertCount={alertCount} alertCountStale={alertCountStale} showChangePw={showChangePw} setShowChangePw={setShowChangePw}
        oldPw={oldPw} setOldPw={setOldPw} newPw={newPw} setNewPw={setNewPw}
        pwMessage={pwMessage} setPwMessage={setPwMessage} changingPw={changingPw} setChangingPw={setChangingPw} />
      </AgentAliasProvider>
      </DownloadCartProvider>
    </BrowserRouter>
  );
}

function AppInner({ signOut, user, isAdmin, canExportSms, alertCount, alertCountStale, showChangePw, setShowChangePw, oldPw, setOldPw, newPw, setNewPw, pwMessage, setPwMessage, changingPw, setChangingPw }: any) {
  const { items: cartItems } = useDownloadCart();
  const [blockedIps, setBlockedIps] = useState<{ ip: string; detectedAt: string }[]>([]);

  useEffect(() => {
    if (!isAdmin) return;
    const check = () => {
      api.getBlockedIps().then((data: any) => setBlockedIps(data.blockedIps || [])).catch(() => {});
    };
    check();
    const interval = setInterval(check, 60000); // check every minute
    return () => clearInterval(interval);
  }, [isAdmin]);

  const dismissBlockedIp = async (ip: string) => {
    await api.dismissBlockedIp(ip).catch(() => {});
    setBlockedIps(prev => prev.filter(b => b.ip !== ip));
  };

  return (
    <>
      <div className="app">
        <nav className="sidebar">
          <div className="sidebar-header">
            <img src="/logo-full.png" alt="Pulse Storage" className="sidebar-logo" />
            <span className="user-badge">{user?.signInDetails?.loginId}</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '2px' }}>
              <span style={{ fontSize: '11px', color: isAdmin ? '#00bcd4' : '#e040fb' }}>
                {isAdmin ? 'Admin' : 'Viewer'}
              </span>
              <NasStatusPill />
            </div>
          </div>
          <ul className="nav-links">
            <li><NavLink to="/">Dashboard</NavLink></li>
            <li><NavLink to="/recordings">All Recordings</NavLink></li>
            <li>
              <NavLink to="/download-cart" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                Download Cart
                {cartItems.length > 0 && (
                  <span style={{
                    background: '#00bcd4', color: '#fff', borderRadius: '10px',
                    padding: '1px 6px', fontSize: '10px', fontWeight: 700,
                    minWidth: '16px', textAlign: 'center', lineHeight: '16px',
                    flexShrink: 0,
                  }}>{cartItems.length}</span>
                )}
              </NavLink>
            </li>
            <li><NavLink to="/upload-calls">Upload Calls</NavLink></li>
            {canExportSms && <li><NavLink to="/export-sms">Export SMS</NavLink></li>}
            <li>
              <NavLink to="/quarantine" style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                Quarantine
                {alertCount > 0 && (
                  <span
                    title={alertCountStale ? 'count may be stale (last refresh failed)' : undefined}
                    style={{
                      background: '#e74c3c', color: '#fff', borderRadius: '10px',
                      padding: '1px 6px', fontSize: '10px', fontWeight: 700,
                      minWidth: '16px', textAlign: 'center', lineHeight: '16px',
                      flexShrink: 0,
                    }}>{alertCount}</span>
                )}
              </NavLink>
            </li>
            <li className="nav-section">Systems</li>
            <li><NavLink to="/system/dialedin">DialedIn (Medicare)</NavLink></li>
            <li><NavLink to="/system/pulsedial">PulseDial</NavLink></li>
            <li><NavLink to="/system/vicidial">Vicidial</NavLink></li>
            <li><NavLink to="/system/leadmomentum">LeadMomentum</NavLink></li>
            <li><NavLink to="/system/3cx-legacy">3CX Legacy</NavLink></li>
            {isAdmin && (
              <>
                <li className="nav-section">Admin</li>
                <li><NavLink to="/sftp-setup">Systems</NavLink></li>
                <li><NavLink to="/access">Access Control</NavLink></li>
                <li><NavLink to="/users">User Access</NavLink></li>
                <li><NavLink to="/api-keys">API Keys</NavLink></li>
                <li><NavLink to="/export-formats">Export Formats</NavLink></li>
                <li><NavLink to="/compliance-report">Compliance Report</NavLink></li>
                <li><NavLink to="/agent-aliases">Agent Aliases</NavLink></li>
                <li><NavLink to="/operations">System Status</NavLink></li>
                <li><NavLink to="/ghl-locations">GHL Locations</NavLink></li>
              </>
            )}
          </ul>
          <div style={{ padding: '8px 16px 16px' }}>
            <button
              style={{ width: '100%', padding: '6px', margin: '0 0 8px 0', border: 'none', background: 'transparent', color: '#888', fontSize: '12px', cursor: 'pointer', transition: 'color 0.15s' }}
              onMouseEnter={e => { e.currentTarget.style.color = '#fff'; }}
              onMouseLeave={e => { e.currentTarget.style.color = '#888'; }}
              onClick={() => setShowChangePw(true)}
            >Change Password</button>
            <button className="sign-out-btn" style={{ width: '100%', margin: 0 }} onClick={signOut}>Sign Out</button>
          </div>
        </nav>
        <main className="main-content">
          {blockedIps.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              {blockedIps.map(b => (
                <div key={b.ip} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 16px', background: '#fff3e0', border: '1px solid #ffb74d', borderRadius: 8, marginBottom: 8 }}>
                  <span style={{ fontSize: 13, color: '#e65100' }}>
                    Unknown IP <strong>{b.ip}</strong> attempted SFTP connection — <a href="/access" style={{ color: '#0097a7' }}>whitelist it</a> if expected
                  </span>
                  <button onClick={() => dismissBlockedIp(b.ip)}
                    style={{ background: 'none', border: 'none', color: '#999', cursor: 'pointer', fontSize: 18, padding: '0 4px' }}>&times;</button>
                </div>
              ))}
            </div>
          )}
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/recordings" element={<Recordings />} />
            <Route path="/search" element={<Search />} />
            <Route path="/quarantine" element={<Quarantine />} />
            <Route path="/download-cart" element={<DownloadCart />} />
            <Route path="/upload-calls" element={<UploadCalls />} />
            <Route path="/system/:systemId" element={<SystemView />} />
            <Route path="/sftp-setup" element={isAdmin ? <SftpSetup /> : <Navigate to="/" />} />
            <Route path="/access" element={isAdmin ? <AccessControl /> : <Navigate to="/" />} />
            <Route path="/users" element={isAdmin ? <Users /> : <Navigate to="/" />} />
            <Route path="/api-keys" element={isAdmin ? <ApiKeys /> : <Navigate to="/" />} />
            <Route path="/export-formats" element={isAdmin ? <ExportFormats /> : <Navigate to="/" />} />
            <Route path="/compliance-report" element={isAdmin ? <ComplianceReport /> : <Navigate to="/" />} />
            <Route path="/agent-aliases" element={isAdmin ? <AgentAliases /> : <Navigate to="/" />} />
            <Route path="/export-sms" element={canExportSms ? <ExportSms /> : <Navigate to="/" />} />
            <Route path="/operations" element={isAdmin ? <Operations /> : <Navigate to="/" />} />
            <Route path="/ghl-locations" element={isAdmin ? <GhlLocations /> : <Navigate to="/" />} />
          </Routes>
        </main>
      </div>
      {showChangePw && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, maxWidth: 400, width: '90%' }}>
            <h3 style={{ margin: '0 0 16px 0' }}>Change Password</h3>
            {pwMessage && (
              <div style={{ padding: '8px 12px', marginBottom: 12, borderRadius: 6, background: pwMessage.type === 'success' ? '#e8f5e9' : '#ffebee', color: pwMessage.type === 'success' ? '#2e7d32' : '#c62828', fontSize: 13 }}>
                {pwMessage.text}
              </div>
            )}
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 13, color: '#666', display: 'block', marginBottom: 4 }}>Current Password</label>
              <input type="password" value={oldPw} onChange={e => setOldPw(e.target.value)}
                style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid #ddd', fontSize: 14, boxSizing: 'border-box' }} />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 13, color: '#666', display: 'block', marginBottom: 4 }}>New Password</label>
              <input type="password" value={newPw} onChange={e => setNewPw(e.target.value)}
                style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid #ddd', fontSize: 14, boxSizing: 'border-box' }} />
              <p style={{ margin: '4px 0 0', fontSize: 11, color: '#999' }}>Minimum 12 characters: uppercase, lowercase, number, symbol</p>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" disabled={changingPw} onClick={async () => {
                if (!oldPw || !newPw) { setPwMessage({ text: 'Both fields required', type: 'error' }); return; }
                setChangingPw(true);
                try {
                  await updatePassword({ oldPassword: oldPw, newPassword: newPw });
                  setPwMessage({ text: 'Password changed successfully', type: 'success' });
                  setOldPw(''); setNewPw('');
                  setTimeout(() => { setShowChangePw(false); setPwMessage(null); }, 2000);
                } catch (err: any) {
                  setPwMessage({ text: err.message || 'Failed to change password', type: 'error' });
                }
                setChangingPw(false);
              }}
                style={{ padding: '8px 20px', borderRadius: 6, border: 'none', background: changingPw ? '#999' : '#00bcd4', color: '#fff', fontSize: 14, cursor: 'pointer' }}>
                {changingPw ? 'Changing...' : 'Change Password'}
              </button>
              <button type="button" onClick={() => { setShowChangePw(false); setPwMessage(null); setOldPw(''); setNewPw(''); }}
                style={{ padding: '8px 20px', borderRadius: 6, border: '1px solid #ddd', background: '#fff', fontSize: 14, cursor: 'pointer' }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function App() {
  // Status page is public — no auth required
  if (window.location.pathname === '/status') {
    return <StatusPage />;
  }

  return (
    <LoginShell>
      {({ signOut, user }) => <AppContent signOut={signOut} user={user} />}
    </LoginShell>
  );
}

export default App;
